'use client';

import { AUTH_TOKEN } from '@/utils/consts';
import { BodySecondary, H2, H4 } from '@liveauctioneers/hammer-ui-core/text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Callout } from '@liveauctioneers/hammer-ui-core/callout';
import { getAuthTokenCookieOptions } from '@/utils/auth/authTokenCookie';
import { getDecodedToken } from '@/utils/getDecodedToken';
import { isErrorResponse } from '@/data/utils';
import { jwtDecodedAtom } from '@/components/Login/atoms';
import { MouseEventHandler } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { useAppContextSetters } from '@/providers/AppContextProvider/AppContextProvider';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { usePostLogin } from '@/data/auth/usePostLogin';
import { useRouter } from '@/navigation-client';
import { useSetAtom } from 'jotai';
import Cookies from 'universal-cookie';

type LoginForm = {
    password: string;
    username: string;
};

type LoginProps = {
    passwordReset?: boolean;
    redirectTo?: string;
};

export const Login = ({ passwordReset, redirectTo }: LoginProps) => {
    const { push } = useRouter();
    const { setToken, setUserDecoded } = useAppContextSetters();
    const setJwtDecoded = useSetAtom(jwtDecodedAtom);

    const { isError, isPending, mutateAsync: postLogin } = usePostLogin();

    const { executeRecaptcha } = useGoogleReCaptcha();

    const {
        clearErrors,
        formState: { errors },
        handleSubmit,
        register,
        setError,
    } = useForm<LoginForm>({
        defaultValues: {
            password: '',
            username: '',
        },
    });

    const incorrectCredentialsMessage = 'Your email address or password is incorrect. Try again.';

    const onSubmit: SubmitHandler<LoginForm> = async (data) => {
        clearErrors();
        try {
            let recaptchaToken = '';
            try {
                if (executeRecaptcha) {
                    recaptchaToken = await executeRecaptcha('login');
                }
            } catch {
                // reCAPTCHA unavailable or not yet loaded — proceed without token
            }

            const payload = await postLogin({ ...data, recaptchaToken });

            if (isErrorResponse(payload)) {
                setError('password', { message: incorrectCredentialsMessage });
                return;
            }

            if (!payload || typeof payload !== 'object' || typeof payload.token !== 'string' || !payload.token) {
                setError('password', { message: incorrectCredentialsMessage });
                return;
            }

            const decoded = getDecodedToken(payload.token);
            const cookies = new Cookies();
            cookies.set(AUTH_TOKEN, payload.token, getAuthTokenCookieOptions(decoded));
            setJwtDecoded(decoded);
            setUserDecoded(decoded);
            setToken(payload.token);
            const isValidRedirect =
                typeof redirectTo === 'string' && redirectTo.startsWith('/') && !redirectTo.startsWith('//');
            push(isValidRedirect ? redirectTo : '/auction');
        } catch {
            setError('password', { message: incorrectCredentialsMessage });
            return;
        }
    };

    const forgotPassword: MouseEventHandler = () => {
        push('/reset-password');
    };

    return (
        <div className="flex w-full flex-col gap-lg">
            {passwordReset !== undefined && (
                <div
                    className="w-full"
                    data-testid="password-reset-callout"
                >
                    <Callout
                        message={passwordReset ? undefined : 'Please try again or contact Support'}
                        status={passwordReset ? 'success' : 'critical'}
                        title={passwordReset ? 'Password reset successful' : 'Unable to reset password'}
                    />
                </div>
            )}
            <div
                className="space-between mt-lg flex w-full flex-col items-center rounded-lg border border-border-secondary bg-background-primary p-md xsMax:border-none"
                data-testid="login-container"
                data-wb-id="Login"
            >
                <H2 data-testid="welcomeBack">
                    Welcome back <span aria-hidden="true">👋</span>
                </H2>
                <div className="mt-2xs flex justify-center">
                    <BodySecondary>Enter your login details to get started</BodySecondary>
                </div>
                <form
                    className="mt-md flex w-full flex-col gap-md"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <TextInput
                        data-testid="username"
                        errorText={errors.username?.message}
                        hasError={Boolean(errors.username) || isError}
                        labelText="Email Address"
                        {...register('username', {
                            required: 'Enter a user name.',
                        })}
                        autoComplete="username"
                        disabled={isPending}
                        type="text"
                    />
                    <TextInput
                        data-testid="password"
                        errorText={errors.password?.message}
                        hasError={Boolean(errors.password) || isError}
                        labelText="Password"
                        {...register('password', {
                            required: 'Enter a password.',
                        })}
                        autoComplete="current-password"
                        disabled={isPending}
                        type="password"
                    />
                    <Button
                        data-testid="loginButton"
                        loading={isPending}
                        type="submit"
                    >
                        Login
                    </Button>
                    <Button
                        data-testid="forgotPasswordButton"
                        onClick={forgotPassword}
                        type="button"
                        variant="transparent"
                    >
                        Forgot Password
                    </Button>
                </form>
            </div>
            <div
                className="flex flex-col items-center justify-center gap-sm text-center"
                data-testid="apply-to-sell-container"
            >
                <H4>Want to sell on Proxibid?</H4>
                <Button
                    data-testid="apply-to-sell-button"
                    onClick={() => push('https://www.proxibid.com/asp/CreateAuctioneer1.asp')}
                    size="sm"
                    variant="outlined"
                >
                    Apply to sell
                </Button>
            </div>
        </div>
    );
};

Login.displayName = 'Login';
